import { useEffect, useId, useRef, useState } from 'react';
import { expenseVisual } from './expense-visual';

/** La barre et ses traits restent vectoriels ; les pourcentages sont du texte
 * HTML à 12 px. Ils ne rétrécissent donc pas avec le viewBox sur téléphone.
 * Chaque côté espace ses étiquettes indépendamment, sans déformer les parts. */
export function StackedPaymentsBar({
  parts,
}: {
  readonly parts: readonly { name: string; basisPoints: number | null }[];
}): React.JSX.Element | null {
  const id = useId().replaceAll(':', '');
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(320);
  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.width > 0) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const positive = parts.filter(
    (part): part is { name: string; basisPoints: number } =>
      part.basisPoints !== null && part.basisPoints > 0,
  );
  if (positive.reduce((sum, part) => sum + part.basisPoints, 0) !== 10000) return null;
  const perRow = Math.max(1, Math.floor(width / 60));
  const rows = Math.max(1, Math.ceil(Math.ceil(positive.length / 2) / perRow));
  const barY = rows * 24 + 8;
  const barHeight = 12;
  const height = barY + barHeight + rows * 24 + 8;
  const gap = 60000 / width;
  let offset = 0;
  const layout = positive.map((part, index) => {
    const from = offset;
    offset += part.basisPoints / 10;
    return {
      ...part,
      from,
      width: part.basisPoints / 10,
      center: (from + offset) / 2,
      label: (from + offset) / 2,
      above: index % 2 === 0,
      row: Math.floor(index / 2) % rows,
    };
  });
  for (const above of [true, false]) {
    for (let row = 0; row < rows; row += 1) {
      const labels = layout.filter((part) => part.above === above && part.row === row);
      for (let i = 0; i < labels.length; i += 1)
        labels[i]!.label = Math.max(
          gap / 2,
          labels[i]!.center,
          i ? labels[i - 1]!.label + gap : gap / 2,
        );
      if ((labels.at(-1)?.label ?? 0) > 1000 - gap / 2) {
        labels.at(-1)!.label = 1000 - gap / 2;
        for (let i = labels.length - 2; i >= 0; i -= 1)
          labels[i]!.label = Math.min(labels[i]!.label, labels[i + 1]!.label - gap);
      }
    }
  }
  const percentage = (value: number): string =>
    `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(value / 100)} %`;
  return (
    <div
      ref={ref}
      className="expense-distribution__stack"
      style={{ height }}
      role="img"
      aria-label={`Répartition sur 100 % : ${layout.map((part) => `${part.name} ${percentage(part.basisPoints)}`).join(', ')}`}
    >
      <svg viewBox={`0 0 1000 ${height}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <clipPath id={id}>
            <rect x="0" y={barY} width="1000" height={barHeight} rx="5" />
          </clipPath>
        </defs>
        <g clipPath={`url(#${id})`}>
          {layout.map((part) => (
            <rect
              key={part.name + part.from}
              x={part.from}
              y={barY}
              width={part.width}
              height={barHeight}
              fill={expenseVisual(part.name).color}
            />
          ))}
        </g>
        {layout.map((part) => (
          <path
            key={part.name + part.from}
            d={
              part.above
                ? `M ${part.label} ${part.row * 24 + 17} L ${part.center} ${barY - 3}`
                : `M ${part.center} ${barY + barHeight + 3} L ${part.label} ${barY + barHeight + part.row * 24 + 15}`
            }
            stroke={expenseVisual(part.name).color}
            fill="none"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      {layout.map((part) => (
        <span
          key={part.name + part.from}
          className="expense-distribution__percentage"
          aria-hidden="true"
          style={{
            left: `${part.label / 10}%`,
            top: part.above ? part.row * 24 : barY + barHeight + part.row * 24 + 16,
            color: expenseVisual(part.name).color,
          }}
        >
          {percentage(part.basisPoints)}
        </span>
      ))}
    </div>
  );
}
